<template>
  <div class="grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
    <!-- Left Column: Image -->
    <div class="md:col-span-3 flex flex-col items-start">
      <img
        :src="company.image_url || company.logo_url || company.logo_path || defaultImage"
        alt="Company Logo"
        class="shadow-md rounded-lg w-[200px] h-[200px] object-cover"
      >

      <!-- File input for uploading a new logo, visible only when editing -->
      <FileUpload
        v-if="isEditing"
        mode="basic"
        accept="image/*"
        :auto="true"
        choose-label="Upload Logo"
        :disabled="isUploadingImage"
        class="mt-4 w-full"
        @select="onImageUpload"
      />
      <div
        v-if="isUploadingImage"
        class="mt-2 text-sm text-gray-500"
      >
        Uploading...
      </div>
    </div>

    <!-- Right Column: Form Fields -->
    <div class="md:col-span-9">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Company Name -->
        <div class="p-2">
          <label
            for="company_name"
            class="block text-gray-700 mb-2 font-medium"
          >Company Name</label>
          <InputText
            id="company_name"
            :model-value="company.company_name || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter company name"
            @update:model-value="updateField('company_name', $event)"
          />
        </div>

        <!-- Official Name -->
        <div class="p-2">
          <label
            for="official_name"
            class="block text-gray-700 mb-2 font-medium"
          >Official Name</label>
          <InputText
            id="official_name"
            :model-value="company.official_name || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter official name"
            @update:model-value="updateField('official_name', $event)"
          />
        </div>

        <!-- Registration Number -->
        <div class="p-2">
          <label
            for="registration_number"
            class="block text-gray-700 mb-2 font-medium"
          >Registration Number</label>
          <InputText
            id="registration_number"
            :model-value="company.registration_number || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter registration number"
            @update:model-value="updateField('registration_number', $event)"
          />
        </div>

        <!-- Tax Number -->
        <div class="p-2">
          <label
            for="tax_number"
            class="block text-gray-700 mb-2 font-medium"
          >Tax Number</label>
          <InputText
            id="tax_number"
            :model-value="company.tax_number || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter tax number"
            @update:model-value="updateField('tax_number', $event)"
          />
        </div>

        <!-- Website -->
        <div class="p-2">
          <label
            for="website"
            class="block text-gray-700 mb-2 font-medium"
          >Website</label>
          <InputText
            id="website"
            :model-value="company.website || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter website URL"
            @update:model-value="updateField('website', $event)"
          />
        </div>

        <!-- Company Status -->
        <div class="p-2">
          <label
            for="company_status"
            class="block text-gray-700 mb-2 font-medium"
          >Status</label>
          <Dropdown
            id="company_status" 
            :model-value="company.company_status || 'active'" 
            :options="statusOptions" 
            option-label="label"
            option-value="value"
            :disabled="!isEditing" 
            class="w-full" 
            @update:model-value="updateField('company_status', $event)"
          />
        </div>

        <!-- Contact Email -->
        <div class="p-2">
          <label
            for="contact_email"
            class="block text-gray-700 mb-2 font-medium"
          >Contact Email</label>
          <InputText
            id="contact_email"
            :model-value="company.contact_email || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter contact email"
            @update:model-value="updateField('contact_email', $event)"
          />
        </div>

        <!-- Accounts Email -->
        <div class="p-2">
          <label
            for="accounts_email"
            class="block text-gray-700 mb-2 font-medium"
          >Accounts Email</label>
          <InputText
            id="accounts_email"
            :model-value="company.accounts_email || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter accounts email"
            @update:model-value="updateField('accounts_email', $event)"
          />
        </div>

        <!-- Phone Number -->
        <div class="p-2">
          <label
            for="phone_number"
            class="block text-gray-700 mb-2 font-medium"
          >Phone Number</label>
          <InputText
            id="phone_number"
            :model-value="company.phone_number || ''"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter phone number"
            @update:model-value="updateField('phone_number', $event)"
          />
        </div>

        <!-- Discount -->
        <div class="p-2">
          <label
            for="discount"
            class="block text-gray-700 mb-2 font-medium"
          >Discount (%)</label>
          <InputNumber
            id="discount"
            :model-value="company.discount || 0"
            :disabled="!isEditing"
            class="w-full"
            placeholder="Enter discount percentage"
            @update:model-value="updateField('discount', $event)"
          />
        </div>

        <!-- Payment Term -->
        <div class="p-2">
          <label
            for="payment_term_id"
            class="block text-gray-700 mb-2 font-medium"
          >Payment Term</label>
          <Dropdown
            id="payment_term_id"
            :model-value="company.payment_term_id || null"
            :options="paymentTermsOptions"
            option-label="label"
            option-value="value"
            :disabled="!isEditing || loadingPaymentTerms"
            :loading="loadingPaymentTerms"
            placeholder="Select payment term"
            class="w-full"
            @update:model-value="updateField('payment_term_id', $event)"
          />
        </div>

        <!-- Payment Status -->
        <div class="p-2">
          <label
            for="payment_status"
            class="block text-gray-700 mb-2 font-medium"
          >Payment Status</label>
          <Dropdown
            id="payment_status"
            :model-value="company.payment_status || null"
            :options="paymentStatusOptions"
            option-label="label"
            option-value="value"
            :disabled="!isEditing"
            placeholder="Select payment status"
            class="w-full"
            @update:model-value="updateField('payment_status', $event)"
          />
        </div>

        <!-- Description -->
        <div class="p-2 md:col-span-2">
          <label
            for="description"
            class="block text-gray-700 mb-2 font-medium"
          >Description</label>
          <Textarea
            id="description"
            :model-value="company.description || ''"
            :disabled="!isEditing"
            class="w-full"
            rows="3"
            placeholder="Enter company description"
            @update:model-value="updateField('description', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import { getPaymentTerms } from '@/services/paymentTermsService';
import defaultImage from "@/assets/icons/Company.svg";

const props = defineProps({
    company: {
        type: Object,
        required: true
    },
    isEditing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:company']);

const toast = useToast();
const isUploadingImage = ref(false);
const paymentTerms = ref([]);
const loadingPaymentTerms = ref(false);

const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
];

const paymentStatusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Cash Only', value: 'cash_only' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Suspended', value: 'suspended' },
    { label: 'Pre Pay Only', value: 'pre_pay_only' }
];

// Payment terms options for dropdown
const paymentTermsOptions = computed(() => {
    const options = paymentTerms.value
        .filter(term => term.is_active)
        .map(term => ({
            label: `${term.name}${term.days > 0 ? ` (${term.days} days)` : ' (Due on Receipt)'}`,
            value: term.id
        }));
    
    return [
        { label: 'None', value: null },
        ...options
    ];
});

// Load payment terms
const loadPaymentTerms = async () => {
    loadingPaymentTerms.value = true;
    try {
        const response = await getPaymentTerms({ active: true }); // Only get active payment terms for dropdown
        paymentTerms.value = response.data || response || [];
        
        // Sort by sort_order, then name
        paymentTerms.value.sort((a, b) => {
            if (a.sort_order !== b.sort_order) {
                return a.sort_order - b.sort_order;
            }
            return a.name.localeCompare(b.name);
        });
    } catch (error) {
        console.error('Error loading payment terms:', error);
        toast.add({
            severity: 'warn',
            summary: 'Load Failed',
            detail: 'Failed to load payment terms',
            life: 5000
        });
        paymentTerms.value = [];
    } finally {
        loadingPaymentTerms.value = false;
    }
};

const updateField = (field, value) => {
    const updatedCompany = { ...props.company, [field]: value };
    emit('update:company', updatedCompany);
};

onMounted(() => {
    loadPaymentTerms();
});

const onImageUpload = async (event) => {
    const file = event.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
        toast.add({
            severity: 'error',
            summary: 'Invalid File Type',
            detail: 'Please upload a JPEG, PNG, GIF, WebP, or SVG image',
            life: 5000
        });
        return;
    }
    
    // Validate file size (10MB max - backend allows 10MB for company images)
    if (file.size > 10 * 1024 * 1024) {
        toast.add({
            severity: 'error',
            summary: 'File Too Large',
            detail: 'Image must be less than 10MB',
            life: 5000
        });
        return;
    }
    
    // Try to get image upload handler from parent (for add mode)
    const handleImageUpload = inject('handleImageUpload', null);
    
    // If company doesn't have an ID (add mode), use parent handler
    if (!props.company.id && handleImageUpload) {
        handleImageUpload(file);
        return;
    }
    
    // If company has ID (edit mode), upload immediately
    if (props.company.id) {
        isUploadingImage.value = true;
        
        try {
            // Import uploadCompanyImage dynamically to avoid circular dependency
            const { uploadCompanyImage } = await import('@/services/companyService');
            
            // Upload logo
            const response = await uploadCompanyImage(props.company.id, file);
            
            // Update company with new logo URL
            // Backend returns: { success: true, message: "...", data: { image_url, logo_path } }
            const updatedCompany = {
                ...props.company,
                image_url: response.data?.image_url || response.data?.logo_url || props.company.image_url,
                logo_url: response.data?.image_url || response.data?.logo_url || props.company.logo_url,
                logo_path: response.data?.logo_path || props.company.logo_path,
                image_path: response.data?.image_path || response.data?.logo_path || props.company.image_path
            };
            emit('update:company', updatedCompany);
            
            toast.add({
                severity: 'success',
                summary: 'Logo Uploaded',
                detail: 'Company logo updated successfully!',
                life: 5000
            });
        } catch (error) {
            console.error('Error uploading logo:', error);
            const errorMessage = error.response?.data?.message || 'Failed to upload logo';
            toast.add({
                severity: 'error',
                summary: 'Upload Failed',
                detail: errorMessage,
                life: 7000
            });
        } finally {
            isUploadingImage.value = false;
        }
    }
};
</script> 